import {
  Coverages,
  PremiumBreakdown,
  SavingsBreakdown,
  SimulationWarning,
} from '../../../playground/endpoints/_shared';
import { simulateV2Product } from '../../api';
import { UniversitySavingsV2FormValues } from './schema';

export const SLUG = 'university-savings-insurance';

/**
 * A `projection_by_year` row, as `output/yearly.py` groups it. The
 * withdrawal trio and `withdrawable_amount` are `debug`-only, but this page
 * always asks for debug, so they arrive on every row.
 */
export interface UniversitySavingsV2YearlyRow {
  year: number;
  age: number;
  actuarial_age: number;
  accumulated_prime: number;
  policy_value: number;
  death_compensation: number;
  coverage_costs: number;
  policy_expenses: number;
  withdrawal: number;
  rentability: number;
  withdrawable_amount: number;
  policy_value_before_withdrawal: number;
  policy_value_after_withdrawal: number;
  [key: string]: number | undefined;
}

/**
 * A `projection_by_month` row, as `output/monthly.py` builds it. No
 * `calendar_month` here -- unlike APV Select and Life Max, this product's
 * monthly rows carry no calendar column.
 */
export interface UniversitySavingsV2MonthlyRow {
  month: number;
  year: number;
  age: number;
  actuarial_age: number;
  policy_value: number;
  rentability: number;
  net_contribution: number;
  cost: number;
  agreed_premium: number;
  accumulated_prime: number;
  coverage_costs: number;
  coverage_costs_breakdown?: Record<string, number>;
  policy_expenses: number;
  death_compensation: number;
  withdrawable_amount: number;
  withdrawal: number;
  policy_value_before_withdrawal: number;
  policy_value_after_withdrawal: number;
}

export interface UniversitySavingsV2ResponseBeneficiary {
  date_of_birth: string;
  start_date: string;
  duration_years: number;
  // The FUNDED tuition (requested x coverage_factor), not the requested one.
  annual_tuition: number;
}

/**
 * `output/serializer.py` plus `output/extra_output.py`. Unlike Family
 * Savings, `coverage_factor`/`terminal_excess`/`objective_achieved_month`
 * are unconditional here -- `build_extra_output` always returns them,
 * regardless of `savings_mode`.
 */
export interface UniversitySavingsV2Response {
  uf_value: number;
  currency: string;
  timeframe_months: number;
  premium: PremiumBreakdown;
  coverages: Coverages;
  savings: SavingsBreakdown;
  projection_by_year: UniversitySavingsV2YearlyRow[];
  projection_by_month?: UniversitySavingsV2MonthlyRow[];
  warnings: SimulationWarning[];
  beneficiaries: UniversitySavingsV2ResponseBeneficiary[];
  coverage_factor: number;
  terminal_excess: number;
  objective_achieved_month: number;
}

/**
 * The rate fields as `UniversitySavingsInput` wants them -- same shape as
 * Family Savings', not `_shared`'s `buildRatePayload`: `rate_mode` must
 * travel, v2's stepped mode is named `tiered`, and the schedule rides as a
 * real JSON array rather than a stringified query param.
 */
function buildRatePayload(values: UniversitySavingsV2FormValues) {
  return values.rate_mode === 'fixed'
    ? { rate_mode: 'fixed', projection_rate: values.projection_rate }
    : {
      rate_mode: 'tiered',
      projection_rate_schedule: values.projection_rate_schedule ?? [],
    };
}

export function buildUniversitySavingsV2Payload(values: UniversitySavingsV2FormValues) {
  const {
    rate_mode: _rateMode,
    projection_rate: _projectionRate,
    projection_rate_schedule: _projectionRateSchedule,
    ...rest
  } = values;

  return {
    ...rest,
    ...buildRatePayload(values),
    // Asks the engine for `projection_by_month`, and keeps the debug-only
    // columns on the yearly rows that this page's tables read.
    debug: true,
  };
}

export async function simulateUniversitySavingsV2(
  values: UniversitySavingsV2FormValues,
): Promise<UniversitySavingsV2Response> {
  return simulateV2Product<UniversitySavingsV2Response>(
    SLUG,
    buildUniversitySavingsV2Payload(values),
  );
}
